import { useEffect, useState } from "react";
import type { Eta } from "@veyso/core";
import { countdown, foldEta, type Held } from "./eta.js";

/** Per job id, across components: the menu, the chip and the page show the same number. */
const memory = new Map<string, Held>();
const TICK_MS = 5_000;

/**
 * The time left to show for several jobs at once, each smoothed against what
 * was shown last and counted down between polls. One hook for a list, so the
 * chip can read every job without calling a hook in a loop.
 */
export function useEtas(items: { key: string; eta: Eta | null | undefined }[]): (Eta | null)[] {
  const [now, setNow] = useState(() => Date.now());

  // Fold a new estimate in once, when it arrives — not on every tick, and not
  // once per component showing the same job.
  const at = Date.now();
  for (const item of items) foldEta(memory, item.key, item.eta, at);

  const counting = items.some((item) => item.eta?.kind === "estimate");
  useEffect(() => {
    if (!counting) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [counting]);

  return items.map((item) => {
    // Only an estimate counts down; a job that finished or reads "slow" here
    // says so, whatever another place still remembers for it.
    const held = item.eta?.kind === "estimate" ? memory.get(item.key) : undefined;
    if (!held) return item.eta ?? null;
    return { kind: "estimate", seconds: countdown(held.shown, Math.max(now, held.last)) };
  });
}

/** The time left to show for one job, keyed by its id. */
export function useEta(key: string, eta: Eta | null | undefined): Eta | null {
  return useEtas([{ key, eta }])[0];
}
